/**
 * Télémétrie client — ABBUSINESS
 *
 * Objectif : qu'une erreur survenue dans le navigateur d'un client laisse une
 * trace côté serveur. Jusqu'ici, une exception React partait dans la console
 * du client et nulle part ailleurs — un tunnel de commande cassé pouvait le
 * rester des semaines sans que personne ne l'apprenne.
 *
 * Deux destinations, indépendantes :
 *   1. Sentry SDK (@sentry/react) — si initialisé (VITE_SENTRY_DSN présent).
 *   2. POST /v1/client-errors — toujours. Visible dans les journaux Laravel.
 *
 * La télémétrie ne doit jamais casser la page : tout est encapsulé.
 */

import { Sentry } from './sentry'
import { API_BASE } from './apiBase'

const ENDPOINT = `${API_BASE}/client-errors`

/** Anti-boucle : une même signature n'est rapportée qu'une fois par session. */
const seen = new Set()

/** Anti-inondation : 10 rapports par session au maximum. */
const MAX_REPORTS = 10
let reportCount = 0

function signature(error, context) {
  return `${context?.source ?? 'unknown'}|${error?.name ?? ''}|${error?.message ?? String(error)}`.slice(0, 300)
}

function shouldReport(error, context) {
  if (reportCount >= MAX_REPORTS) return false

  const sig = signature(error, context)
  if (seen.has(sig)) return false

  seen.add(sig)
  reportCount += 1
  return true
}

/**
 * Rapporte une erreur.
 * @param {Error|unknown} error
 * @param {{source?: string, componentStack?: string, [k: string]: unknown}} context
 */
export function reportError(error, context = {}) {
  try {
    if (import.meta.env.DEV) {
      console.error(`[${context.source ?? 'app'}]`, error, context)
    }

    if (!shouldReport(error, context)) return

    // ── Sentry SDK ────────────────────────────────────────────────────────
    // Silencieux si Sentry n'est pas initialisé (DSN absent).
    try {
      Sentry.captureException(error, {
        extra: {
          source:          context.source,
          componentStack:  context.componentStack,
          url:             window.location.pathname + window.location.search,
        },
      })
    } catch {
      // Ne jamais laisser Sentry bloquer le chemin de signalement secondaire.
    }

    // ── Backend (toujours) ────────────────────────────────────────────────
    const payload = {
      message: String(error?.message ?? error ?? 'Unknown error').slice(0, 500),
      name:    String(error?.name ?? 'Error').slice(0, 100),
      stack:   String(error?.stack ?? '').slice(0, 3000),
      source:  String(context.source ?? 'unknown').slice(0, 100),
      component_stack: String(context.componentStack ?? '').slice(0, 2000),
      url:        window.location.pathname + window.location.search,
      user_agent: navigator.userAgent.slice(0, 300),
      app_version: import.meta.env.VITE_APP_VERSION ?? 'dev',
    }

    const body = JSON.stringify(payload)

    // sendBeacon survit à la navigation et au déchargement de la page,
    // là où un fetch classique serait annulé.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }))
      return
    }

    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {})  // meme raison que le catch ci-dessous
  } catch {
    // Une télémétrie qui échoue ne doit rien empêcher.
  }
}

/**
 * Capte les deux catégories d'erreurs qu'un ErrorBoundary React ne voit pas :
 * les exceptions dans les gestionnaires d'événements, et les promesses rejetées.
 */
export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined') return

  window.addEventListener('error', (event) => {
    reportError(event.error ?? new Error(event.message), {
      source: 'window.onerror',
      file: event.filename,
      line: event.lineno,
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason ?? new Error('Unhandled rejection'), {
      source: 'unhandledrejection',
    })
  })

  // Un chunk manquant signifie que l'utilisateur tourne sur une version
  // déployée puis supprimée. Le seul remède est de recharger.
  window.addEventListener('vite:preloadError', (event) => {
    reportError(event.payload ?? new Error('Chunk load failed'), {
      source: 'chunk-load',
    })
    event.preventDefault()
    window.location.reload()
  })
}
